import { parseISO } from 'date-fns';
import { z } from 'zod';
import {
  type FhirTimingRepeat,
  FhirTimingDayOfWeekSchema,
  FhirTimingWhenSchema,
  MAX_OCCURRENCE_COUNT,
} from './fhirTiming';
import { HOURLY_PRESET_PERIODS, MAX_DAYS_PER_WEEK } from './timingShape';

/** Max occurrences per day the weekday shape allows (one per time-of-day chip). */
export const MAX_TIMES_PER_DAY = 4;

const HOURLY_PRESETS = [...HOURLY_PRESET_PERIODS].sort((a, b) => a - b).map(String) as [string, ...string[]];

/** The one field whose wording depends on which item kind the tool is bound to. */
type DaysOfWeekField = z.ZodOptional<z.ZodArray<typeof FhirTimingDayOfWeekSchema>>;

const daysOfWeekFor = (itemNoun: string): DaysOfWeekField =>
  z
    .array(FhirTimingDayOfWeekSchema)
    .min(1)
    .optional()
    .describe(`weekday only: the weekdays the ${itemNoun} is due (list all seven for a daily schedule).`);

/**
 * Every field but `daysOfWeek`, which the factory supplies per item kind. Held as a const so its
 * inferred type names the object shape, letting the factory below carry an explicit return type
 * without restating the schema.
 */
const TIMING_FIELDS = {
  kind: z
    .enum(['weekday', 'daysPerWeek', 'interval', 'hourly'])
    .describe(
      'Which schedule shape this is. "weekday": specific days, N times/day (use for daily/specific-days/N-times-daily) — MUST also set daysOfWeek. ' +
        '"daysPerWeek": any N distinct days per week, no specific days — MUST also set daysPerWeek. ' +
        '"interval": one occurrence every N days/weeks/months/years — MUST also set everyN and unit (weekly = everyN: 1, unit: "wk"). ' +
        '"hourly": every N hours (preset) — MUST also set everyHours.',
    ),
  timesPerDay: z
    .number()
    .int()
    .min(1)
    .max(MAX_TIMES_PER_DAY)
    .optional()
    .describe('weekday only: how many times per day (default 1). If greater than 1, provide that many `when` codes.'),
  when: z
    .array(FhirTimingWhenSchema)
    .optional()
    .describe('weekday only: time-of-day codes (MORN/AFT/EVE/NIGHT). One per occurrence when timesPerDay > 1.'),
  // daysPerWeek
  daysPerWeek: z
    .number()
    .int()
    .min(2)
    .max(MAX_DAYS_PER_WEEK)
    .optional()
    .describe('daysPerWeek only: complete on any N distinct days per week (2–7). Use `weekday` with all 7 for daily.'),
  // interval
  everyN: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('interval only: interval length (e.g. 1 = every, 2 = every other). Defaults to 1 when omitted.'),
  unit: z
    .enum(['d', 'wk', 'mo', 'a'])
    .optional()
    .describe('interval only: interval unit — d (days), wk (weeks), mo (months), a (years).'),
  // hourly
  everyHours: z.enum(HOURLY_PRESETS).optional().describe('hourly only: every N hours (1/2/4/6/8/12).'),
  // shared schedule-end fields (any kind)
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .describe(
      'YYYY-MM-DD date the schedule starts. Anchors intervals/hourly with period > 1 — defaults to today when omitted, so set it only when the schedule genuinely starts on another date.',
    ),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .describe('YYYY-MM-DD date the schedule ends. Omit for an open-ended schedule.'),
  endAfterCount: z
    .number()
    .int()
    .positive()
    .max(MAX_OCCURRENCE_COUNT)
    .optional()
    .describe('End the schedule after this many occurrences. Mutually exclusive with endDate.'),
} as const;

type TimingShape = typeof TIMING_FIELDS & { daysOfWeek: DaysOfWeekField };

/**
 * Build the AI-facing timing schema for one care-plan item kind.
 *
 * `itemNoun` is the word the model reads for the thing being scheduled — "to-do", "medication",
 * "measurement", "habit". It is a parameter rather than a fixed word because the model picks its
 * own vocabulary up off the schema it is given: a to-do tool whose `daysOfWeek` says "the weekdays
 * the task is due" teaches the model to call a to-do a task, in the one field that is in context
 * exactly when it is proposing a schedule (#9820).
 *
 * AI-facing timing input: a single FLAT object whose `kind` discriminant selects
 * one of the four canonical schedule shapes, with each shape's fields present but
 * optional. A `.superRefine` enforces the per-`kind` field rules (which fields are
 * required, which are forbidden), so validation is exactly as strict as a
 * discriminated union — but the shape the model reads is a plain object.
 *
 * Why flat rather than `z.discriminatedUnion`: Gemini/Vertex rejects `anyOf`/`oneOf`
 * anywhere in a tool's function-declaration schema ("when using any_of, it must be
 * the only field set"). A union schema would have to be collapsed to a flat object
 * by the sanitizer anyway — lossily, and with edge cases (nested unions, scalar
 * unions). Authoring it flat means the schema the model receives IS this schema:
 * `kind` as a 4-value enum plus described optional fields, steering the model
 * structurally with no lossy transform. The `.superRefine` keeps the model honest
 * at the tool boundary; `llmTimingToRepeat` maps to the canonical FHIR shape.
 *
 * The shapes mirror the schedule editor's `buildTimingRepeat` (`schedulePicker.ts`)
 * and the classifier's canonical set (`timingShape.ts`).
 */
export const llmTimingUnionSchema = (itemNoun: string): z.ZodObject<TimingShape, z.core.$strip> =>
  z
    .object({ ...TIMING_FIELDS, daysOfWeek: daysOfWeekFor(itemNoun) })
    .describe(
      "A recurring schedule. Set `kind` and only that kind's fields: weekday → daysOfWeek (+timesPerDay/when); " +
        'daysPerWeek → daysPerWeek; interval → everyN + unit; hourly → everyHours. All may set startDate/endDate/endAfterCount. ' +
        'Examples: daily = {kind: "weekday", daysOfWeek: [all seven days]}; weekly = {kind: "interval", everyN: 1, unit: "wk"}; ' +
        'every other day = {kind: "interval", everyN: 2, unit: "d"} (anchored to today unless startDate says otherwise).',
    )
    .superRefine((v, ctx) => {
      const requireField = (field: keyof typeof v, cond: boolean, message: string): void => {
        if (!cond) ctx.addIssue({ code: 'custom', message, path: [field] });
      };

      // Per-kind required/forbidden fields — the discriminated-union guarantee, flat.
      if (v.kind === 'weekday') {
        requireField('daysOfWeek', (v.daysOfWeek?.length ?? 0) >= 1, 'weekday requires a non-empty daysOfWeek.');
        const times = v.timesPerDay ?? 1;
        requireField(
          'when',
          times === 1 || (v.when?.length ?? 0) === times,
          'When timesPerDay > 1, provide exactly that many `when` codes (one per daily occurrence).',
        );
      } else if (v.kind === 'daysPerWeek') {
        requireField('daysPerWeek', v.daysPerWeek !== undefined, 'daysPerWeek requires a daysPerWeek count (2–7).');
      } else if (v.kind === 'interval') {
        // A missing everyN is recoverable (llmTimingToRepeat defaults it to 1); a missing unit is not.
        requireField('unit', v.unit !== undefined, 'interval requires a unit — d, wk, mo, or a (weekly = unit: "wk").');
        // A missing anchor for everyN > 1 is *also* recoverable — callers apply `withDefaultScheduleAnchor`
        // before converting, so it is no longer rejected here (#8626).
      } else if (v.kind === 'hourly') {
        requireField('everyHours', v.everyHours !== undefined, 'hourly requires everyHours.');
      }

      // Shared schedule-end rules (any kind).
      requireField(
        'endDate',
        !(v.endDate !== undefined && v.endAfterCount !== undefined),
        'Provide at most one of endDate or endAfterCount.',
      );
      requireField(
        'endDate',
        !v.startDate || !v.endDate || parseISO(v.startDate).getTime() <= parseISO(v.endDate).getTime(),
        'endDate must not be before startDate.',
      );
    });

/**
 * The kind-agnostic instance, for callers with no one item kind in view — the schedule-phrasing
 * evals, the UI preview path, and the `LlmTimingUnion` type. A tool bound to a single kind should
 * build its own with that kind's noun instead.
 */
export const LlmTimingUnionSchema = llmTimingUnionSchema('item');

export type LlmTimingUnion = z.infer<typeof LlmTimingUnionSchema>;

// Parse YYYY-MM-DD via date-fns parseISO (local midnight), matching schedulePicker's buildTimingRepeat.
const toDate = (dateStr: string): Date => parseISO(dateStr);

/** Attach `boundsPeriod`/`count` from the shared schedule-end fields. */
function withScheduleEnd(
  repeat: FhirTimingRepeat,
  input: { startDate?: string; endDate?: string; endAfterCount?: number },
): FhirTimingRepeat {
  const next = { ...repeat };
  if (input.startDate || input.endDate) {
    next.boundsPeriod = {
      ...(input.startDate ? { start: toDate(input.startDate) } : {}),
      ...(input.endDate ? { end: toDate(input.endDate) } : {}),
    };
  }
  if (input.endAfterCount !== undefined) next.count = input.endAfterCount;
  return next;
}

/**
 * Whether this schedule needs a `startDate` anchor to be resolvable: `getPeriodWindow` computes
 * calendar-aligned windows for `period === 1` but anchor-relative ones above it, and throws without
 * `boundsPeriod.start`. Only the two multi-period shapes can be affected — `weekday` and
 * `daysPerWeek` are always `period: 1`.
 */
export function needsScheduleAnchor(input: LlmTimingUnion): boolean {
  if (input.kind === 'interval') return (input.everyN ?? 1) > 1;
  if (input.kind === 'hourly') return Number(input.everyHours) > 1;
  return false;
}

/**
 * Supply the anchor an interval/hourly schedule needs when the caller left it out (#8626).
 *
 * The schema used to *reject* a `{kind: 'interval', everyN: 3, unit: 'mo'}` with no `startDate` —
 * "check HbA1c every 3 months", the ordinary case in a care-plan bootstrap. That rejection bought
 * nothing: turns are date-prefixed, so the model knows today's date and every observed recovery
 * re-emitted `startDate` as exactly that. Worse, it was not always *reached* — 2 of 10 bootstrap runs
 * ended with a topic carrying no tracked result because the rejected call was never re-proposed, so a
 * provider saw a plan that looked complete and silently wasn't tracking the lab governing it.
 *
 * So this defaults rather than rejects. It changes **who supplies the anchor, not whether one
 * exists** — `getPeriodWindow` still requires `boundsPeriod.start`, and an explicit `startDate` is
 * always preserved. Pass the anchor date in rather than reading the clock here, so callers stay
 * testable and a batch shares one date.
 */
export function withDefaultScheduleAnchor(input: LlmTimingUnion, today: string): LlmTimingUnion {
  if (input.startDate !== undefined || !needsScheduleAnchor(input)) return input;
  return { ...input, startDate: today };
}

/**
 * Map a validated {@link LlmTimingUnion} to a canonical FHIR `Timing.repeat` — the
 * trio-bearing shape storage and the day-view engine expect. Assumes the input
 * passed `LlmTimingUnionSchema` (so the kind's required fields are present); an
 * omitted interval `everyN` defaults to 1 (the schema tolerates it, #7257). The
 * output always classifies canonically (`classifyTimingShape`), by construction.
 */
export function llmTimingToRepeat(input: LlmTimingUnion): FhirTimingRepeat {
  switch (input.kind) {
    case 'weekday': {
      const base: FhirTimingRepeat = {
        frequency: input.timesPerDay ?? 1,
        period: 1,
        periodUnit: 'd',
        dayOfWeek: input.daysOfWeek,
        ...(input.when && input.when.length > 0 ? { when: input.when } : {}),
      };
      return withScheduleEnd(base, input);
    }
    case 'daysPerWeek':
      return withScheduleEnd({ frequency: input.daysPerWeek, period: 1, periodUnit: 'wk' }, input);
    case 'interval':
      return withScheduleEnd({ frequency: 1, period: input.everyN ?? 1, periodUnit: input.unit }, input);
    case 'hourly':
      return withScheduleEnd({ frequency: 1, period: Number(input.everyHours), periodUnit: 'h' }, input);
    default:
      return input.kind satisfies never;
  }
}

/**
 * `llmTimingToRepeat` with the anchor default applied first — the form every AI create/update tool
 * wants, so the defaulting cannot be forgotten at one call site while the others have it. Plain
 * `llmTimingToRepeat` stays exported for the UI preview paths, which render an already-authored
 * suggestion and must not have today's date injected into it.
 */
export function llmTimingToRepeatAnchored(input: LlmTimingUnion, today: string): FhirTimingRepeat {
  return llmTimingToRepeat(withDefaultScheduleAnchor(input, today));
}
